import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { auditLogService } from './auditLogService';

/**
 * Content Flag Interface
 */
export interface ContentFlag {
  id: string;
  contentType: 'person' | 'photo' | 'comment' | 'relationship';
  contentId: string;
  reason: string;
  description?: string;
  reportedBy: string;
  reportedByEmail: string;
  reportedByName: string;
  status: 'pending' | 'approved' | 'rejected' | 'resolved';
  reviewedBy?: string;
  reviewedByEmail?: string;
  reviewedByName?: string;
  reviewedAt?: Date;
  reviewNotes?: string;
  action?: 'none' | 'warning' | 'content_hidden' | 'content_removed' | 'user_suspended';
  createdAt: Date;
}

/**
 * Content Flag Firestore Interface
 */
interface ContentFlagFirestore extends Omit<ContentFlag, 'id' | 'createdAt' | 'reviewedAt'> {
  createdAt: Timestamp;
  reviewedAt?: Timestamp;
}

/**
 * Content Flag Statistics
 */
export interface FlagStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  resolved: number;
  byContentType: {
    person: number;
    photo: number;
    comment: number;
    relationship: number;
  };
  byReason: Record<string, number>;
  recentFlags: ContentFlag[];
}

/**
 * Content Moderation Service
 * Handles content flagging, review, and moderation actions
 *
 * This service provides methods for:
 * - Flagging inappropriate content
 * - Reviewing and resolving flags
 * - Getting flag statistics
 * - Taking moderation actions (hide, remove, warn)
 */
export class ContentModerationService {
  /**
   * Flag content as inappropriate
   * @param contentType - Type of content being flagged
   * @param contentId - ID of content being flagged
   * @param reason - Reason for flagging
   * @param description - Optional detailed description
   * @returns Created flag
   */
  static async flagContent(
    contentType: 'person' | 'photo' | 'comment' | 'relationship',
    contentId: string,
    reason: string,
    description?: string
  ): Promise<ContentFlag> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Must be authenticated to flag content');
      }

      // Check if content is already flagged by this user
      const existingFlags = await this.getUserFlags(currentUser.uid, contentType, contentId);
      if (existingFlags.length > 0) {
        throw new Error('You have already flagged this content');
      }

      const flagData: Omit<ContentFlagFirestore, 'createdAt'> & { createdAt: any } = {
        contentType,
        contentId,
        reason,
        description,
        reportedBy: currentUser.uid,
        reportedByEmail: currentUser.email || 'unknown',
        reportedByName: currentUser.displayName || 'Unknown User',
        status: 'pending',
        createdAt: serverTimestamp(),
      };

      const flagRef = await addDoc(collection(db, 'flags'), flagData);

      console.log('Content flagged:', flagRef.id);

      return {
        id: flagRef.id,
        contentType,
        contentId,
        reason,
        description,
        reportedBy: currentUser.uid,
        reportedByEmail: currentUser.email || 'unknown',
        reportedByName: currentUser.displayName || 'Unknown User',
        status: 'pending',
        createdAt: new Date(),
      };
    } catch (error) {
      console.error('Error flagging content:', error);
      throw error;
    }
  }

  /**
   * Get all flags with optional filters
   * @param filters - Optional filters
   * @returns Array of flags
   */
  static async getFlags(filters?: {
    status?: string;
    contentType?: string;
    reportedBy?: string;
  }): Promise<ContentFlag[]> {
    try {
      const constraints: QueryConstraint[] = [];

      if (filters?.status) {
        constraints.push(where('status', '==', filters.status));
      }

      if (filters?.contentType) {
        constraints.push(where('contentType', '==', filters.contentType));
      }

      if (filters?.reportedBy) {
        constraints.push(where('reportedBy', '==', filters.reportedBy));
      }

      constraints.push(orderBy('createdAt', 'desc'));

      const q = query(collection(db, 'flags'), ...constraints);
      const snapshot = await getDocs(q);

      const flags: ContentFlag[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        flags.push({
          id: doc.id,
          contentType: data.contentType,
          contentId: data.contentId,
          reason: data.reason,
          description: data.description,
          reportedBy: data.reportedBy,
          reportedByEmail: data.reportedByEmail,
          reportedByName: data.reportedByName,
          status: data.status,
          reviewedBy: data.reviewedBy,
          reviewedByEmail: data.reviewedByEmail,
          reviewedByName: data.reviewedByName,
          reviewedAt: data.reviewedAt?.toDate(),
          reviewNotes: data.reviewNotes,
          action: data.action,
          createdAt: data.createdAt?.toDate(),
        });
      });

      return flags;
    } catch (error) {
      console.error('Error getting flags:', error);
      throw new Error('Failed to retrieve content flags');
    }
  }

  /**
   * Get pending flags only
   * @returns Array of pending flags
   */
  static async getPendingFlags(): Promise<ContentFlag[]> {
    return await this.getFlags({ status: 'pending' });
  }

  /**
   * Get flags for specific content
   * @param contentType - Type of content
   * @param contentId - ID of content
   * @returns Array of flags for this content
   */
  static async getContentFlags(
    contentType: 'person' | 'photo' | 'comment' | 'relationship',
    contentId: string
  ): Promise<ContentFlag[]> {
    try {
      const q = query(
        collection(db, 'flags'),
        where('contentType', '==', contentType),
        where('contentId', '==', contentId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);

      const flags: ContentFlag[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        flags.push({
          id: doc.id,
          contentType: data.contentType,
          contentId: data.contentId,
          reason: data.reason,
          description: data.description,
          reportedBy: data.reportedBy,
          reportedByEmail: data.reportedByEmail,
          reportedByName: data.reportedByName,
          status: data.status,
          reviewedBy: data.reviewedBy,
          reviewedByEmail: data.reviewedByEmail,
          reviewedByName: data.reviewedByName,
          reviewedAt: data.reviewedAt?.toDate(),
          reviewNotes: data.reviewNotes,
          action: data.action,
          createdAt: data.createdAt?.toDate(),
        });
      });

      return flags;
    } catch (error) {
      console.error('Error getting content flags:', error);
      throw new Error('Failed to retrieve content flags');
    }
  }

  /**
   * Get flags created by specific user
   * @param userId - User ID
   * @param contentType - Optional content type filter
   * @param contentId - Optional content ID filter
   * @returns Array of flags
   */
  static async getUserFlags(
    userId: string,
    contentType?: string,
    contentId?: string
  ): Promise<ContentFlag[]> {
    try {
      const constraints: QueryConstraint[] = [where('reportedBy', '==', userId)];

      if (contentType) {
        constraints.push(where('contentType', '==', contentType));
      }

      if (contentId) {
        constraints.push(where('contentId', '==', contentId));
      }

      constraints.push(orderBy('createdAt', 'desc'));

      const q = query(collection(db, 'flags'), ...constraints);
      const snapshot = await getDocs(q);

      const flags: ContentFlag[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        flags.push({
          id: doc.id,
          contentType: data.contentType,
          contentId: data.contentId,
          reason: data.reason,
          description: data.description,
          reportedBy: data.reportedBy,
          reportedByEmail: data.reportedByEmail,
          reportedByName: data.reportedByName,
          status: data.status,
          reviewedBy: data.reviewedBy,
          reviewedByEmail: data.reviewedByEmail,
          reviewedByName: data.reviewedByName,
          reviewedAt: data.reviewedAt?.toDate(),
          reviewNotes: data.reviewNotes,
          action: data.action,
          createdAt: data.createdAt?.toDate(),
        });
      });

      return flags;
    } catch (error) {
      console.error('Error getting user flags:', error);
      throw new Error('Failed to retrieve user flags');
    }
  }

  /**
   * Review and resolve a flag
   * @param flagId - Flag ID
   * @param reviewedBy - Admin user ID
   * @param status - New status (approved/rejected/resolved)
   * @param action - Action taken
   * @param reviewNotes - Optional review notes
   */
  static async reviewFlag(
    flagId: string,
    reviewedBy: string,
    status: 'approved' | 'rejected' | 'resolved',
    action: 'none' | 'warning' | 'content_hidden' | 'content_removed' | 'user_suspended',
    reviewNotes?: string
  ): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Must be authenticated to review flags');
      }

      const flagRef = doc(db, 'flags', flagId);
      const flagDoc = await getDoc(flagRef);

      if (!flagDoc.exists()) {
        throw new Error('Flag not found');
      }

      const flagData = flagDoc.data();

      await updateDoc(flagRef, {
        status,
        action,
        reviewNotes,
        reviewedBy,
        reviewedByEmail: currentUser.email || 'unknown',
        reviewedByName: currentUser.displayName || 'Unknown Admin',
        reviewedAt: serverTimestamp(),
      });

      // Log to audit trail
      await auditLogService.logAction({
        userId: reviewedBy,
        action: status === 'rejected' ? 'content_rejected' : 'content_approved',
        contentType: flagData.contentType,
        contentId: flagData.contentId,
        details: {
          flagId,
          status,
          action,
          reviewNotes,
          reportedBy: flagData.reportedBy,
          reason: flagData.reason,
        },
      });

      console.log('Flag reviewed:', flagId, status);
    } catch (error) {
      console.error('Error reviewing flag:', error);
      throw error;
    }
  }

  /**
   * Approve a flag (content is inappropriate)
   * @param flagId - Flag ID
   * @param reviewedBy - Admin user ID
   * @param action - Action to take
   * @param reviewNotes - Optional review notes
   */
  static async approveFlag(
    flagId: string,
    reviewedBy: string,
    action: 'warning' | 'content_hidden' | 'content_removed' | 'user_suspended',
    reviewNotes?: string
  ): Promise<void> {
    await this.reviewFlag(flagId, reviewedBy, 'approved', action, reviewNotes);
  }

  /**
   * Reject a flag (content is appropriate)
   * @param flagId - Flag ID
   * @param reviewedBy - Admin user ID
   * @param reviewNotes - Optional review notes
   */
  static async rejectFlag(
    flagId: string,
    reviewedBy: string,
    reviewNotes?: string
  ): Promise<void> {
    await this.reviewFlag(flagId, reviewedBy, 'rejected', 'none', reviewNotes);
  }

  /**
   * Get flag statistics
   * @returns Flag statistics
   */
  static async getFlagStats(): Promise<FlagStats> {
    try {
      const flags = await this.getFlags();

      const stats: FlagStats = {
        total: flags.length,
        pending: 0,
        approved: 0,
        rejected: 0,
        resolved: 0,
        byContentType: {
          person: 0,
          photo: 0,
          comment: 0,
          relationship: 0,
        },
        byReason: {},
        recentFlags: flags.slice(0, 10),
      };

      flags.forEach((flag) => {
        // Count by status
        if (flag.status === 'pending') stats.pending++;
        else if (flag.status === 'approved') stats.approved++;
        else if (flag.status === 'rejected') stats.rejected++;
        else if (flag.status === 'resolved') stats.resolved++;

        // Count by content type
        stats.byContentType[flag.contentType]++;

        // Count by reason
        stats.byReason[flag.reason] = (stats.byReason[flag.reason] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error getting flag stats:', error);
      throw new Error('Failed to retrieve flag statistics');
    }
  }

  /**
   * Check if content is flagged
   * @param contentType - Type of content
   * @param contentId - ID of content
   * @returns Whether content has pending flags
   */
  static async isContentFlagged(
    contentType: 'person' | 'photo' | 'comment' | 'relationship',
    contentId: string
  ): Promise<boolean> {
    try {
      const flags = await this.getContentFlags(contentType, contentId);
      return flags.some((flag) => flag.status === 'pending');
    } catch (error) {
      console.error('Error checking if content is flagged:', error);
      return false;
    }
  }

  /**
   * Get flag count for content
   * @param contentType - Type of content
   * @param contentId - ID of content
   * @returns Number of pending flags
   */
  static async getFlagCount(
    contentType: 'person' | 'photo' | 'comment' | 'relationship',
    contentId: string
  ): Promise<number> {
    try {
      const flags = await this.getContentFlags(contentType, contentId);
      return flags.filter((flag) => flag.status === 'pending').length;
    } catch (error) {
      console.error('Error getting flag count:', error);
      return 0;
    }
  }

  /**
   * Bulk review flags
   * @param flagIds - Array of flag IDs
   * @param reviewedBy - Admin user ID
   * @param status - New status
   * @param action - Action taken
   * @param reviewNotes - Optional review notes
   * @returns Results
   */
  static async bulkReviewFlags(
    flagIds: string[],
    reviewedBy: string,
    status: 'approved' | 'rejected' | 'resolved',
    action: 'none' | 'warning' | 'content_hidden' | 'content_removed' | 'user_suspended',
    reviewNotes?: string
  ): Promise<{ successCount: number; failedCount: number; errors: string[] }> {
    const results = {
      successCount: 0,
      failedCount: 0,
      errors: [] as string[],
    };

    for (const flagId of flagIds) {
      try {
        await this.reviewFlag(flagId, reviewedBy, status, action, reviewNotes);
        results.successCount++;
      } catch (error: any) {
        results.failedCount++;
        results.errors.push(`${flagId}: ${error.message}`);
      }
    }

    return results;
  }
}

// Export singleton instance
export const contentModerationService = ContentModerationService;
